//
// Copyright © 2026 ZeroDesk
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { validateIban, validateSteuerId, validateSvNummer } from './validators'

/**
 * Deterministic generators for syntactically valid but fictional master data of the demo employees.
 */

/**
 * Small seeded random number generator (mulberry32).
 * @public
 */
export function seededRandom (seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function randomInt (rand: () => number, max: number): number {
  return Math.floor(rand() * max)
}

/**
 * Valid tax ID with one repeated digit.
 * @public
 */
export function generateSteuerId (rand: () => number): string {
  for (let attempt = 0; attempt < 100; attempt++) {
    const pool = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
    for (let i = pool.length - 1; i > 0; i--) {
      const j = randomInt(rand, i + 1)
      ;[pool[i], pool[j]] = [pool[j], pool[i]]
    }
    const digits = pool.slice(0, 9)
    digits.splice(randomInt(rand, 10), 0, digits[randomInt(rand, 9)])
    if (digits[0] === 0) continue
    const prefix = digits.join('')
    for (let check = 0; check <= 9; check++) {
      if (validateSteuerId(prefix + String(check))) return prefix + String(check)
    }
  }
  return '65929970489'
}

/**
 * Valid social security number for the given birth date (UTC timestamp) and first letter of the birth name.
 * @public
 */
export function generateSvNummer (rand: () => number, birthDate: number, letter: string): string {
  const d = new Date(birthDate)
  const area = String(2 + randomInt(rand, 98)).padStart(2, '0')
  const birth =
    String(d.getUTCDate()).padStart(2, '0') +
    String(d.getUTCMonth() + 1).padStart(2, '0') +
    String(d.getUTCFullYear() % 100).padStart(2, '0')
  const serial = String(randomInt(rand, 100)).padStart(2, '0')
  const prefix = `${area}${birth}${letter.toUpperCase().slice(0, 1)}${serial}`
  for (let check = 0; check <= 9; check++) {
    if (validateSvNummer(prefix + String(check))) return prefix + String(check)
  }
  return '15070649C103'
}

/**
 * Valid German IBAN with a fictional bank code and account number.
 * @public
 */
export function generateIban (rand: () => number): string {
  const bank = String(10000000 + randomInt(rand, 89999999))
  const account = String(1000000000 + randomInt(rand, 8999999999))
  for (let check = 2; check <= 98; check++) {
    const iban = `DE${String(check).padStart(2, '0')}${bank}${account}`
    if (validateIban(iban)) return iban
  }
  return 'DE89370400440532013000'
}
